import { endOfDay, parseISO, startOfDay } from 'date-fns';

/**
 * Adapter nowego API list: /alarms, /notifications, /traits, /alarms/stats.
 *
 * Czyste funkcje, bez zależności od Angulara — dzięki temu dają się testować bez
 * TestBed i nie ciągną za sobą HttpClienta.
 *
 * Dwa powody, dla których ten plik w ogóle istnieje:
 *  1. Nowe endpointy zwracają kopertę { status, data: { items, pagination } },
 *     a komponenty spodziewały się gołej tablicy w `data` (a przy /my-notif-*
 *     wręcz tablicy tablic). `readPage` sprowadza to do jednego kształtu.
 *  2. Ten sam typ zdarzenia nazywa się w backendzie na kilka sposobów
 *     (`sos` / `alarmsos`, `zone` / `alarmzone` / `alarmleavezone` / `notifLeaveZone`).
 *     `toCanonicalAlarmType` sprowadza je do jednej postaci, żeby szablony
 *     porównywały się do jednej wartości zamiast do trzech.
 */

export interface Page<T = any> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

/** Wspólne filtry wszystkich trzech list. `from`/`to`: chwila w RFC 3339 (`toApiInstant`). */
export interface ListQuery {
  imei?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface AlarmsQuery extends ListQuery {
  /** sos | fall | zone | call | immo */
  type?: string;
  /** unhandled | in_progress | handled */
  status?: string;
  /**
   * Fraza wyszukiwania: „imię nazwisko" pacjenta ALBO fragment IMEI, podłańcuchowo,
   * bez względu na wielkość liter i polskie znaki. Składa się z pozostałymi filtrami,
   * a `pagination.total` liczy trafienia w całym zakresie, nie na stronie.
   */
  q?: string;
  /** date | handled | patient | imei | type | handler | status — biała lista po stronie API */
  sort?: string;
  /** asc | desc */
  dir?: string;
  /** konkretne alarmy po id (przypięte), np. "12,34" */
  ids?: string;
  /** alarmy nieznanych numerów (bez opaski): `1` — razem z pozostałymi, `only` — tylko one */
  unknown?: string;
}

export interface NotificationsQuery extends ListQuery {
  /** alarm | event | all */
  category?: string;
  type?: string;
  handled?: boolean;
}

export interface TraitsQuery extends ListQuery {
  /** 1 bradykardia, 2 tachykardia, 3 wysoka tachykardia, 4 niska saturacja */
  trait?: number;
  confirmed?: boolean;
  handled?: boolean;
}

/** Wartownik daty z PHP-owego `\DateTime` zerowego — stare API tak oznaczało "brak". */
const NULL_DATE_SENTINELS = [
  '-0001-11-30T00:00:00+00:00',
  '0000-00-00 00:00:00',
  '0000-00-00',
  '1970-01-01T00:00:00+00:00',
  '1970-01-01 00:00:00',
  '1970-01-01'
];

/**
 * Aliasy typów zdarzeń → postać kanoniczna. Klucze zawsze małymi literami,
 * bo backend miesza konwencje (`notifLeaveZone` vs `alarmleavezone`).
 */
const ALARM_TYPE_ALIASES: { [alias: string]: string } = {
  sos: 'sos',
  alarmsos: 'sos',
  notifsos: 'sos',

  fall: 'fall',
  alarmfall: 'fall',
  notiffall: 'fall',

  zone: 'zone',
  alarmzone: 'zone',
  leavezone: 'zone',
  alarmleavezone: 'zone',
  notifleavezone: 'zone',

  call: 'call',
  alarmcall: 'call',
  notifcall: 'call',

  immo: 'immo',
  alarmimmo: 'immo',
  notifimmo: 'immo'
};

/**
 * Sprowadza nazwę typu do postaci używanej w szablonach (`sos`, `fall`, `zone`, ...).
 * Wartości spoza słownika (`pulse`, `low_battery`, `band_worn`...) przechodzą bez zmian —
 * to typy powiadomień, których nie ma sensu przepisywać.
 */
export function toCanonicalAlarmType(raw: any): string {
  if (raw === null || raw === undefined) {
    return '';
  }
  const value = String(raw).trim();
  if (!value) {
    return '';
  }
  // Backend potrafi przysłać FQCN encji (App\Entity\notifLeaveZone) — bierzemy ostatni człon.
  const shortName = value.split('\\').pop();
  return ALARM_TYPE_ALIASES[shortName.toLowerCase()] || shortName;
}

/**
 * Odrzuca puste filtry, żeby nie wysyłać `?type=&status=` — backend odpowiada 400
 * na nieznaną wartość filtra, a pusty string jest właśnie nieznaną wartością.
 * Wynik to mapa stringów, bo HttpParams w Angularze 11 nie przyjmuje liczb ani booli.
 */
export function toQueryParams(query: { [key: string]: any }): { [key: string]: string } {
  const params: { [key: string]: string } = {};
  if (!query) {
    return params;
  }
  Object.keys(query).forEach(key => {
    const value = query[key];
    if (value === null || value === undefined || value === '') {
      return;
    }
    params[key] = String(value);
  });
  return params;
}

/**
 * Wyciąga stronę z koperty `{ status, data: { items, pagination } }`.
 *
 * Toleruje też odpowiedź w starym kształcie (gołą tablicę w `data`) — nie po to,
 * żeby ukryć błąd, tylko dlatego, że dev i prod bywają wdrażane osobno i przez ten
 * czas lista ma się wyświetlać. `total` jest wtedy liczbą pobranych wierszy, więc
 * paginacja pokaże jedną stronę zamiast kłamać o liczbie stron.
 */
export function readPage<T = any>(response: any, fallbackLimit: number = 0): Page<T> {
  const data = response && response.data ? response.data : response;

  if (Array.isArray(data)) {
    return { items: data, total: data.length, limit: fallbackLimit, offset: 0, hasMore: false };
  }

  const items: T[] = data && Array.isArray(data.items) ? data.items : [];
  const pagination = (data && data.pagination) || {};

  const total = Number(pagination.total);
  const limit = Number(pagination.limit);
  const offset = Number(pagination.offset);

  return {
    items,
    total: isFinite(total) ? total : items.length,
    limit: isFinite(limit) ? limit : fallbackLimit,
    offset: isFinite(offset) ? offset : 0,
    hasMore: pagination.has_more === true
  };
}

/**
 * Kod HTTP wyłuskany z błędu, który oddaje `ApiService`. Ten rozpakowuje odpowiedź
 * (`error.error || error`), więc status bywa raz na wierzchu, a raz o poziom głębiej.
 * Pusty string, gdy żądanie nie doszło w ogóle — wtedy nie ma czego pokazać.
 */
export function readApiErrorStatus(error: any): string {
  const status = (error && error.status) || (error && error.error && error.error.status) || '';
  return status ? String(status) : '';
}

/** Pierwsza wartość, która naprawdę czymś jest. Pusty string liczy się jako brak. */
export function firstDefined(...values: any[]): any {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '') {
      return value;
    }
  }
  return null;
}

/** `true` dla daty, która naprawdę coś znaczy (nie null i nie wartownik). */
export function isRealDate(value: any): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  return NULL_DATE_SENTINELS.indexOf(String(value)) === -1;
}

/**
 * Data z API → ISO. Backend podaje ją w dwóch postaciach, z których `| date` nie
 * odczyta żadnej: unix w sekundach (wziąłby je za milisekundy) i zapis SQL bez
 * strefy (patrz `withUtcZone`).
 */
export function toIsoDate(value: any): any {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const asNumber = Number(value);
  if (isFinite(asNumber) && /^\d{9,11}$/.test(String(value).trim())) {
    return new Date(asNumber * 1000).toISOString();
  }
  return withUtcZone(value);
}

/** Data bez żadnego oznaczenia strefy: "2026-08-14 16:44:58" albo z 'T' zamiast spacji. */
const ZONELESS_DATE = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})$/;

/**
 * Dopisuje 'Z' dacie zapisanej bez strefy. Backend trzyma te kolumny w UTC, ale
 * serializuje je gołym zapisem SQL — a datę bez offsetu JavaScript parsuje jako czas
 * LOKALNY. Bez tego kolumna rozjeżdżałaby się z datą alarmu (unix → poprawnie
 * lokalna) o offset strefy: latem w Polsce o dwie godziny.
 *
 * Wartości, które strefę już niosą (ISO z offsetem, unix), wracają bez zmian.
 */
export function withUtcZone(value: any): any {
  if (typeof value !== 'string') {
    return value;
  }
  const match = value.trim().match(ZONELESS_DATE);
  return match ? `${match[1]}T${match[2]}Z` : value;
}

const RFC3339_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

export function instantMs(value: any): number | null {
  if (typeof value === 'number') {
    return isFinite(value) && value !== 0 ? Math.round(value * 1000) : null;
  }
  if (typeof value !== 'string' || !isRealDate(value)) {
    return null;
  }
  const text = value.trim();
  if (/^\d{9,11}$/.test(text)) {
    return Number(text) * 1000;
  }
  const iso = withUtcZone(text);
  const ms = RFC3339_INSTANT.test(iso) ? Date.parse(iso) : NaN;
  return isNaN(ms) ? null : ms;
}

export function toApiInstant(value: Date | string): string {
  const ms = value instanceof Date ? value.getTime() : instantMs(value);
  return ms === null || isNaN(ms) ? '' : new Date(ms).toISOString();
}

export function toApiDayStart(day: string): string {
  return toApiInstant(startOfDay(parseISO(day)));
}

export function toApiDayEnd(day: string): string {
  return toApiInstant(endOfDay(parseISO(day)));
}

function splitName(raw: any): { firstName: string, lastName: string } {
  const parts = String(raw || '').trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') };
}

/**
 * Sprowadza wiersz z /alarms lub /notifications do pól, którymi posługują się
 * szablony. Backend nie zadeklarował kształtu pojedynczego elementu, więc czytamy
 * zarówno camelCase, jak i snake_case i zostawiamy oryginalne pola nietknięte.
 */
function normalizeRow(raw: any): any {
  if (!raw) {
    return raw;
  }

  // `time_come` / `received_date` / `handled_date` to goły UTC bez strefy — bez `withUtcZone`
  // pipe `date` brał je za czas lokalny i pokazywał 2 h do tyłu (patrz `cplDate` niżej).
  const timeCome = toIsoDate(withUtcZone(firstDefined(
    raw.timeCome, raw.time_come, raw.receivedDate, raw.received_date,
    raw.createdAt, raw.created_at, raw.date
  )));

  const timeHandleRaw = firstDefined(
    raw.timeHandle, raw.time_handle, raw.handledDate, raw.handled_date
  );
  const timeHandle = isRealDate(timeHandleRaw) ? toIsoDate(withUtcZone(timeHandleRaw)) : null;

  // Przyjście ramki CPL = zakończenie procedury alarmowej; null, dopóki ramka nie przyszła.
  const cplRaw = firstDefined(raw.comeCplDate, raw.come_cpl_date);
  const cplDate = isRealDate(cplRaw) ? toIsoDate(cplRaw) : null;

  const type = toCanonicalAlarmType(firstDefined(raw.notifType, raw.notif_type, raw.type, raw.discr));

  let firstName = firstDefined(raw.firstName, raw.first_name);
  let lastName = firstDefined(raw.lastName, raw.last_name);
  const name = firstDefined(raw.name, raw.patientName, raw.patient_name);
  if (!firstName && name) {
    const split = splitName(name);
    firstName = split.firstName;
    lastName = lastName || split.lastName;
  }

  const handled = typeof raw.handled === 'boolean'
    ? raw.handled
    : (String(firstDefined(raw.status, '')) === 'handled' || timeHandle !== null);

  const status = firstDefined(raw.status) || (handled ? 'handled' : 'unhandled');

  return {
    ...raw,
    id: firstDefined(raw.id, raw.alarmId, raw.alarm_id, raw.notifId, raw.notif_id),
    imei: firstDefined(raw.imei, raw.bandImei, raw.band_imei),
    firstName: firstName || '',
    lastName: lastName || '',
    name: name || `${firstName || ''} ${lastName || ''}`.trim(),
    timeCome,
    timeHandle,
    cplDate,
    whoOpen: firstDefined(raw.whoOpen, raw.who_open, raw.handledBy, raw.handled_by),
    // Alarm z pamięci opaski — szablony pokazują przy nim plakietkę „zaległy”.
    isHrStore: !!firstDefined(raw.isHrStore, raw.is_hr_store),
    handledDate: timeHandle,
    notifType: type,
    type,
    handled,
    status
  };
}

export function normalizeAlarmRow(raw: any): any {
  return normalizeRow(raw);
}

export function normalizeNotificationRow(raw: any): any {
  return normalizeRow(raw);
}

/**
 * Wiersz z /traits. Ten endpoint istniał wcześniej i szablon czyta z niego
 * `trait`, `traitId`, `bandImei`, `receivedDate` — pilnujemy, żeby te pola były,
 * niezależnie od tego, w której konwencji przyjdą.
 */
export function normalizeTraitRow(raw: any): any {
  if (!raw) {
    return raw;
  }

  const confirmedRaw = firstDefined(raw.trait_confirmed, raw.traitConfirmed, raw.confirmed);
  const handledRaw = firstDefined(raw.isHandled, raw.is_handled, raw.handled);
  const handledDate = firstDefined(raw.handledDate, raw.handled_date);
  const split = splitName(raw.name);

  return {
    ...raw,
    traitId: firstDefined(raw.traitId, raw.trait_id, raw.id),
    trait: firstDefined(raw.trait, raw.traitCode, raw.trait_code, raw.code),
    bandImei: firstDefined(raw.bandImei, raw.band_imei, raw.imei),
    name: raw.name || '',
    firstName: firstDefined(raw.firstName, raw.first_name) || split.firstName,
    lastName: firstDefined(raw.lastName, raw.last_name) || split.lastName,
    receivedDate: toIsoDate(withUtcZone(firstDefined(raw.receivedDate, raw.received_date, raw.date))),
    handledDate: isRealDate(handledDate) ? toIsoDate(withUtcZone(handledDate)) : null,
    trait_confirmed: (Number(confirmedRaw) === 1 || confirmedRaw === true) ? 1 : 0,
    isHandled: (Number(handledRaw) === 1 || handledRaw === true) ? 1 : 0
  };
}

export interface TraitReasonLine {
  key: string;
  params: any;
}

const TRAIT_REASON_KEYS = {
  confirmed: 'TRAIT_R_CONFIRMED', cancelled: 'TRAIT_R_CANCELLED', cancelled_day: 'TRAIT_R_CANCELLED_DAY',
  expired: 'TRAIT_R_EXPIRED', expired_night: 'TRAIT_R_EXPIRED_NIGHT', pending: 'TRAIT_R_PENDING', hr_store: 'TRAIT_R_HR_STORE'
};

/**
 * Powód potwierdzenia / braku potwierdzenia z /traits/explain jako linie tekstu (klucz i18n + parametry).
 * `translate` tłumaczy etykietę pewności, bo ta idzie jako parametr innego klucza.
 */
export function traitReasonLines(t: any, translate: (key: string) => string): TraitReasonLine[] {
  const r = t && t.reason;
  if (!r) {
    return [];
  }
  const unit = Number(t.trait) === 4 ? '%' : '';
  const day = (d: Date) => d.getFullYear() + '-' + d.getMonth() + '-' + d.getDate();
  const two = (n: number) => (n < 10 ? '0' : '') + n;
  const at = r.at ? new Date(r.at) : null;
  const detectedAt = t.receivedDate ? new Date(t.receivedDate) : null;
  const time = !at ? '' : (detectedAt && day(at) === day(detectedAt) ? '' : two(at.getDate()) + '.' + two(at.getMonth() + 1) + ' ')
    + two(at.getHours()) + ':' + two(at.getMinutes());
  const params = {
    time, count: r.in_range, required: r.required, min: r.window_min,
    values: (r.values || []).map(v => v + unit).join(', '),
    value: r.cancel_value != null ? r.cancel_value + unit : ''
  };
  const lines: TraitReasonLine[] = [{ key: 'USER_DETAILS.' + (TRAIT_REASON_KEYS[r.outcome] || 'TRAIT_R_PENDING'), params }];
  if (r.detect) {
    const trust = r.detect.trust >= 3 ? 'COMMON.HIGH_CONFIDENCE' : r.detect.trust === 2 ? 'COMMON.MEDIUM_CONFIDENCE' : 'COMMON.LOW_CONFIDENCE';
    lines.unshift({ key: 'USER_DETAILS.TRAIT_R_DETECT', params: { value: r.detect.value + unit, trust: translate(trust) } });
  }
  if (r.legacy) {
    lines.push({ key: 'USER_DETAILS.TRAIT_R_LEGACY', params: {} });
  }
  if (r.mismatch) {
    lines.push({ key: 'USER_DETAILS.TRAIT_R_MISMATCH', params: {} });
  }
  return lines;
}

/**
 * Sekcja statystyk (`alarms`, `notifications`, `traits`) z /alarms/stats.
 * Zwraca zawsze komplet liczb, żeby kafelki nie pokazywały `undefined`,
 * gdy backend pominie którąś sekcję.
 */
export interface StatsCounts {
  total: number;
  unhandled: number;
  inProgress: number;
  handled: number;
}

function toCount(value: any): number {
  const parsed = Number(value);
  return isFinite(parsed) ? parsed : 0;
}

export function readStatsCounts(section: any): StatsCounts {
  const counts = (section && section.counts) || {};
  return {
    total: toCount(counts.total),
    unhandled: toCount(counts.unhandled),
    inProgress: toCount(counts.in_progress),
    handled: toCount(counts.handled)
  };
}

/**
 * Ile pozycji czeka na zamknięcie: nieobsłużone plus podjęte, ale niezamknięte
 * (`in_progress`).
 *
 * Lista traktuje je tak samo — `isUnhandled()` sprawdza wyłącznie `handled !== true`,
 * więc wiersz `in_progress` dostaje przycisk „Obsłuż". Kafelek liczył tylko
 * `unhandled` i przez to nie sumował się z „obsłużonymi" do `counts.total`
 * (u managera 17 + 478 przy 506 wszystkich).
 *
 * Powiadomienia nie mają tego stanu — `inProgress` jest tam zerem, więc ta sama
 * funkcja obsługuje obie sekcje.
 */
export function countUnclosed(counts: StatsCounts): number {
  return counts.unhandled + counts.inProgress;
}

/**
 * Typy powiadomień, które backend zalicza do kategorii `alarm`. Kategoria `event`
 * jest ich dopełnieniem (`notif_type NOT IN (...)`), więc tą samą listą odsiewamy
 * je z liczników. Musi być identyczna z `NotificationQuery::ALARM_TYPES` w backendzie.
 *
 * Bezruch (`immo`) NIE jest tu alarmem: w `notification_board` to powiadomienie,
 * które ma trafić do zakładki „Powiadomienia" (alarm bezruchu zakładka „Alarmy"
 * czyta z osobnej tabeli). Z `immo` na tej liście kafelek pomijałby wiersze,
 * które lista pod nim pokazuje.
 */
const ALARM_NOTIFICATION_TYPES = ['sos', 'fall', 'zone'];

/**
 * Liczniki sekcji `notifications` ograniczone do kategorii `event` — czyli do tego
 * samego zbioru, który pokazuje lista pod kafelkami (`/notifications?category=event`).
 *
 * `notifications.counts` obejmuje WSZYSTKIE wiersze `notification_board`, także
 * sos/fall/zone/immo. To te same zdarzenia, które zakładka „Alarmy" liczy z tabeli
 * alarmów, więc kafelek podpisany „Powiadomienia" pokazywał liczbę wielokrotnie
 * większą od listy pod nim (na dev w całej tabeli: 41 608 wierszy alarmowych wobec
 * 57 608 zdarzeń). Rozbicie `by_type` przychodzi w tej samej odpowiedzi, więc
 * sumujemy tylko kubełki spoza listy alarmowej — bez dodatkowego żądania.
 *
 * Odpowiedź bez `by_type` (starsze wdrożenie backendu) czytamy po staremu: zawyżony
 * licznik jest mniej mylący niż wyzerowany.
 */
export function readNotificationEventCounts(section: any): StatsCounts {
  const byType = section && section.by_type;

  if (!byType || typeof byType !== 'object') {
    return readStatsCounts(section);
  }

  const counts: StatsCounts = { total: 0, unhandled: 0, inProgress: 0, handled: 0 };

  Object.keys(byType).forEach(type => {
    if (ALARM_NOTIFICATION_TYPES.indexOf(String(type).toLowerCase()) !== -1) {
      return;
    }
    const bucket = readStatsCounts({ counts: byType[type] });
    counts.total += bucket.total;
    counts.unhandled += bucket.unhandled;
    counts.inProgress += bucket.inProgress;
    counts.handled += bucket.handled;
  });

  return counts;
}
